import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { formatShortRelativeDate } from '~/utils/formatShortRelativeDate';

interface UseShortRelativeDateOptions {
  nowLabel?: string;
}

interface FormatOptions {
  withAgo?: boolean;
}

/**
 * Returns a formatter that produces a localized short relative date string
 * (e.g. `now` / `5m` / `3h` / `Wednesday` / `Mar 5`). The "now" label is
 * pulled from i18n (or overridden via `nowLabel`); callers can opt into the
 * `ago` suffix per call.
 */
export const useShortRelativeDate = ({ nowLabel }: UseShortRelativeDateOptions = {}) => {
  const { t } = useTranslation();
  return useCallback(
    (input: Date | string | null | undefined, { withAgo = false }: FormatOptions = {}) =>
      formatShortRelativeDate(input, {
        nowLabel: nowLabel ?? t('community.messages.relative_time.now', 'now'),
        formatMinutes: (count) => t('community.messages.relative_time.minutes', '{{count}}m', { count }),
        formatHours: (count) => t('community.messages.relative_time.hours', '{{count}}h', { count }),
        formatAgo: withAgo ? (time) => t('community.messages.relative_time.ago', '{{time}} ago', { time }) : undefined,
      }),
    [t, nowLabel],
  );
};
